/**
 * Game Session API Service
 * 
 * Handles all API calls for game session functionality
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  errorCode?: string;
  message?: string;
}

interface GameSession {
  session_id: string;
  user_wallet: string;
  initial_bet: number;
  rounds_completed: number;
  session_status: 'active' | 'cashed_out' | 'busted' | 'expired';
  final_payout?: number;
  created_at: string;
  completed_at?: string;
}

interface SignedRaceDetails {
  round_id: number;
  start_timestamp: number;
  end_timestamp: number;
  cars_with_odds: Array<{
    car: string;
    odds: number;
  }>;
  signature: number[];
}

interface SignedResults {
  round_id: number;
  results: string[];
  timestamp: number;
  signature: number[];
}

interface GameRound {
  round_id: string;
  session_id: string;
  round_number: number;
  phase: 'BETTING' | 'RACING' | 'SETTLEMENT' | 'RESULTS';
  champion: string;
  challenger: string;
  available_challenger_pool: string[];
  player_pick?: string;
  betting_start_time?: string;
  betting_end_time?: string;
  race_start_time?: string;
  race_end_time?: string;
  starting_prices?: Record<string, number>;
  ending_prices?: Record<string, number>;
  race_winner?: string;
  player_won?: boolean;
  player_decision?: string;
  completed_at?: string;
  signed_race_details?: SignedRaceDetails;  // For on-chain betting
  signed_results?: SignedResults;  // For on-chain cash out/continue
}

interface PayoutLadderEntry {
  round: number;
  multiplier: number;
}

interface PayoutLadder {
  ladder: PayoutLadderEntry[];
  config: {
    houseVig: number;
    baseMultiplier: number;
    maxRounds: number;
    finalMultiplier: number;
  };
}

interface MatchupHistory {
  assetA: string;
  assetB: string;
  winRateA: number;
  winRateB: number;
  totalRaces: number;
  recentWins: {
    winner: string;
    timestamp: number;
    priceChangeA: number;
    priceChangeB: number;
  }[];
}

interface TickerResult {
  wallet: string;      // Truncated wallet address
  won: boolean;        // true = won, false = busted
  multiplier: number;  // Multiplier they reached
  timestamp: string;   // ISO timestamp
}

// Transaction History Types
interface UserStats {
  gamesPlayed: number;
  totalWagered: number;
  netProfit: number;
  highestLevelWon: number;
}

interface SessionSummary {
  session_id: string;
  initial_bet: number;
  final_payout: number | null;
  rounds_completed: number;
  session_status: 'cashed_out' | 'busted';
  created_at: string;
}

interface HistoryResponse {
  stats: UserStats;
  sessions: SessionSummary[];
  hasMore: boolean;
}

// Price data stored in JSONB format: { "BTC": 43250.75, "ETH": 2250.25, ... }
interface PriceData {
  [asset: string]: number;
}

// Price feed ID mapping: { "BTC": "e62df6c8b4a85fe1a67db44dc12de5db330f7ac66b72dc658afedf0f4a415b43", ... }
interface PriceFeedIds {
  [asset: string]: string;
}

interface RoundDetail {
  round_id: string;
  round_number: number;
  champion: string;
  challenger: string;
  player_pick: string | null;
  race_winner: string | null;
  player_won: boolean | null;
  player_decision: string | null;
  race_start_time: string | null;
  race_end_time: string | null;
  starting_prices: PriceData | null;
  ending_prices: PriceData | null;
  on_chain_bet_tx: string | null;
  created_at: string;
}

interface SessionRoundsResponse {
  session: SessionSummary & {
    user_wallet: string;
    completed_at: string | null;
  };
  rounds: RoundDetail[];
  priceFeedIds: PriceFeedIds;
}

class GameApiService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = API_BASE_URL;
  }

  /**
   * Create a new game session
   */
  async createSession(userWallet: string, initialBet: number): Promise<ApiResponse<{
    session: GameSession;
    round: GameRound;
  }>> {
    try {
      const response = await fetch(`${this.baseUrl}/api/game/create-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          userWallet,
          initialBet
        })
      });

      return await response.json();
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to create session'
      };
    }
  }

  /**
   * Pick an asset during BETTING phase (with on-chain transaction)
   */
  async pickAsset(
    sessionId: string,
    playerPick: string,
    transactionSignature: string,
    userWallet: string
  ): Promise<ApiResponse<{ round: GameRound }>> {
    try {
      const response = await fetch(`${this.baseUrl}/api/game/pick-asset/${sessionId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          playerPick,
          transactionSignature,
          userWallet
        })
      });

      return await response.json();
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to pick asset'
      };
    }
  }

  /**
   * Cash out during RESULTS phase (after winning)
   */
  async cashOut(sessionId: string, userWallet: string, transactionSignature: string): Promise<ApiResponse<{
    session: GameSession;
    payout: number;
  }>> {
    try {
      const response = await fetch(`${this.baseUrl}/api/game/cash-out/${sessionId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          userWallet,
          transactionSignature
        })
      });

      return await response.json();
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to cash out'
      };
    }
  }

  /**
   * Continue to next round during RESULTS phase (after winning)
   */
  async continue(sessionId: string, userWallet: string): Promise<ApiResponse<{
    round: GameRound;
  }>> {
    try {
      
      const response = await fetch(`${this.baseUrl}/api/game/continue/${sessionId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          userWallet
        })
      });

      const result = await response.json();
      
      return result;
    } catch (error: any) {
      console.error('❌ Continue API error:', error);
      return {
        success: false,
        error: error.message || 'Failed to continue'
      };
    }
  }

  /**
   * Get session by ID
   */
  async getSession(sessionId: string): Promise<ApiResponse<{
    session: GameSession;
  }>> {
    try {
      const response = await fetch(`${this.baseUrl}/api/game/session/${sessionId}`);
      return await response.json();
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to get session'
      };
    }
  }

  /**
   * Get specific round by session ID and round number
   */
  async getRound(sessionId: string, roundNumber: number): Promise<ApiResponse<{
    round: GameRound;
  }>> {
    try {
      const response = await fetch(`${this.baseUrl}/api/game/round/${sessionId}/${roundNumber}`);
      return await response.json();
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to get round'
      };
    }
  }

  /**
   * Get active session for a wallet
   */
  async getActiveSession(userWallet: string): Promise<ApiResponse<{
    hasActiveSession: boolean;
    session: GameSession | null;
    round: GameRound | null;
  }>> {
    try {
      const response = await fetch(`${this.baseUrl}/api/game/active-session/${userWallet}`);
      return await response.json();
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to get active session'
      };
    }
  }

  /**
   * Get payout ladder (static)
   */
  async getPayoutLadder(): Promise<ApiResponse<PayoutLadder>> {
    try {
      const response = await fetch(`${this.baseUrl}/api/game/payout-ladder`);
      return await response.json();
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to get payout ladder'
      };
    }
  }

  /**
   * Get matchup history for two assets
   */
  async getMatchupHistory(
    assetA: string,
    assetB: string,
    limit: number = 10
  ): Promise<ApiResponse<{
    history: MatchupHistory;
    matchup: {
      assetA: string;
      assetB: string;
      raceDuration: number;
    };
  }>> {
    try {
      const response = await fetch(
        `${this.baseUrl}/api/game/matchup-history?assetA=${assetA}&assetB=${assetB}&limit=${limit}`
      );
      return await response.json();
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to get matchup history'
      };
    }
  }

  /**
   * Get recent results for activity ticker
   */
  async getRecentResults(limit: number = 50): Promise<ApiResponse<TickerResult[]>> {
    try {
      const response = await fetch(`${this.baseUrl}/api/game/recent-results?limit=${limit}`);
      return await response.json();
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to get recent results'
      };
    }
  }

  /**
   * Get system stats
   */
  async getStats(): Promise<ApiResponse<{
    totalActiveSessions: number;
    totalActiveUsers: number;
    totalSessions: number;
    totalUsers: number;
    serverTime: string;
  }>> {
    try {
      const response = await fetch(`${this.baseUrl}/api/game/stats`);
      return await response.json();
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to get stats'
      };
    }
  }

  /**
   * Get user's game history with stats and paginated sessions
   * Only returns completed sessions (cashed_out, busted)
   */
  async getUserHistory(
    userWallet: string,
    limit: number = 20,
    offset: number = 0
  ): Promise<ApiResponse<HistoryResponse>> {
    try {
      const response = await fetch(
        `${this.baseUrl}/api/game/history/${userWallet}?limit=${limit}&offset=${offset}`
      );
      return await response.json();
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to get user history'
      };
    }
  }

  /**
   * Get all rounds for a specific session (drill-down view)
   */
  async getSessionRounds(sessionId: string): Promise<ApiResponse<SessionRoundsResponse>> {
    try {
      const response = await fetch(`${this.baseUrl}/api/game/session-rounds/${sessionId}`);
      return await response.json();
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to get session rounds'
      };
    }
  }
}

// Export singleton instance
export const gameApiService = new GameApiService();

// Export types
export type {
  ApiResponse,
  GameSession,
  GameRound,
  PayoutLadder,
  PayoutLadderEntry,
  MatchupHistory,
  SignedRaceDetails,
  SignedResults,
  TickerResult,
  UserStats,
  SessionSummary,
  HistoryResponse,
  RoundDetail,
  SessionRoundsResponse,
  PriceData,
  PriceFeedIds
};
